import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { UTILS } from "utils";
import { API } from "api";
import { errorHandler } from "api/api/errorHandler";
import { LoaderAction } from "redux/actions";
import AppContainer from "components/ui/AppContainer";
import AppPaginatedTable from "components/ui/AppPaginatedTable";
import AppSearchInput from "components/ui/AppSearchInput";
import AppButton from "components/ui/AppButton.jsx";
import AppIcon from 'components/ui/AppIcon'

const ViewAllPaymentLinks = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [paymentLinks, setPaymentLinks] = useState([]);
  const { isLoading } = useSelector((state) => state.LoaderReducer);
  const [searchTerm, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const history = useHistory();
  const getAllPaymentLinks = useCallback(async () => {
    try {
      dispatch(LoaderAction.setLoading(true));
      const userDetail = UTILS.getCurrentUser();
      const result = await API.GET_ALL_PAYMENT_LINKS({
        brandId: userDetail.brandId,
      });
      if (result?.success === 1 && result?.data) {
        setPaymentLinks(result.data);
      } else {
        UTILS.errorNotification(errorHandler(result));
      }
    } catch (e) {
      UTILS.errorNotification(errorHandler(e));
    } finally {
      dispatch(LoaderAction.setLoading(false));
    }
  }, []);

  useEffect(() => {
    getAllPaymentLinks();

    return () => {
      setPaymentLinks([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const LINKS = useMemo(() => {
    if (searchTerm === "" || searchTerm === null) {
      return paymentLinks;
    }
    return paymentLinks.filter((link) => {
      const cartIdString = link.cartId.toString();
      return cartIdString.includes(searchTerm);
    });
  }, [paymentLinks, searchTerm]);

  /**
   * sets the value of `e` to the `search` variable.
   */
  const handSearch = (e) => {
    setSearch(e);
  };

  const PAID_STATUS = "PAID";
    const renderStatus = (val) => (
      <span
        className={`${
          val === PAID_STATUS
            ? "bg-green-50 text-green-600"
            : "bg-yellow-50 text-yellow-600"
        } w-fit px-2 py-1`}
      >
        {val === PAID_STATUS ? t("Paid") : t("Pending")}
      </span>
    );
  
    const redirectToPaymentLink = (paymentLink) => {
      history.push("/app/dashboard/payment-link-detail", {
        paymentLink: paymentLink,
      });
    };
    const getPaymentLinkUrl = (val, row) => {
      const userDetail = UTILS.getCurrentUser();
      const storeFrontURL = userDetail?.storeFrontURL;
      if (storeFrontURL && row?.cartId && userDetail?.temporaryDomainName) {
        const paymentLink = `${storeFrontURL}${userDetail.temporaryDomainName}/payment-link/${row?.cartId}`;
        navigator.clipboard.writeText(paymentLink);
        UTILS.successNotification(t("Copied to Clipboard"));
      }
    };
    const renderActions = (val, row, index) => {
      return (
        <div className="flex items-center  gap-2">
          <span className="cursor-pointer" onClick={() => {
              redirectToPaymentLink(row);
            }}>
            <AppIcon name="eye"/>
          </span>
          <span className="cursor-pointer" onClick={() => {
              getPaymentLinkUrl(val, row);
            }}>
              <AppIcon name="copy" />
            </span>
        </div>
      );
    };
   const columns = [
      {
        width: "25%",
        title: t("Store name"),
        dataIndex: "storeName",
        align: "initial",
        key: "storeName",
      },
      {
        title: t("Payment link id"),
        dataIndex: "cartId",
        key: "cartId",
        sorter: {
          compare: (a, b) => a.cartId - b.cartId,
          multiple: 3,
        },
      },
      // {
      //   title: t("Payment gateway"),
      //   dataIndex: "paymentGateway",
      //   key: "paymentGateway",
      // },
      {
        key: t("Amount"),
        title: t("Amount"),
        dataIndex: "amount",
        sorter: {
          compare: (a, b) => a.amount - b.amount,
          multiple: 3,
        },
      },
      {
        title: t("Creation date"),
        dataIndex: "linkCreationDate",
        key: "linkCreationDate",
        render: (val, row, index) => {
          return moment.unix(row.linkCreationDate).format("DD/MM/YYYY");
        },
        sorter: {
          compare: (a, b) => a.linkCreationDate - b.linkCreationDate,
          multiple: 3,
        },
      },
  
      {
        title: t("Created by"),
        dataIndex: "createdBy",
        key: "createdBy",
      },
      {
        title: t("Status"),
        dataIndex: "linkStatus",
        key: "status",
        render: renderStatus,
      },
  
      {
        title: t("Action"),
        dataIndex: "action",
        key: "action",
      fixed: "right",
        render: renderActions,
      },
    ];

  return (
    <AppContainer title={t("Payment Links")} subTitle={t("Manage all your payment links here.")} loading={isLoading}>
      <div className="my-3 flex flex-wrap items-center justify-end gap-3">
        <div className="min-w-[150px] max-w-md flex-1">
          <AppSearchInput
            placeholder={t("Search by link id")}
            onInput={(e) => handSearch(e.target.value)}
          />
        </div>
        <AppButton
          icon={<ReloadOutlined />}
          onClick={() => getAllPaymentLinks()}
        >
          {t("Refresh")}
        </AppButton>
        <AppButton
          icon={<PlusOutlined />}
          onClick={() =>
            history.push("/app/dashboard/create-payment-links")
          }
        >
          {t("Create Payment Link")}
        </AppButton>
      </div>
        <AppPaginatedTable columns={columns} data={LINKS} pageSize={pageSize} page={page} total={LINKS.length} onPageChange={(page, pageSize) => {setPage(page); setPageSize(pageSize)}} clientSidePagination={true} />
    </AppContainer>
  );
};
export default ViewAllPaymentLinks;
